import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { ArrowLeft, CornerUpLeft, Forward, MailOpen, Loader2, Send, Paperclip,
         Languages, RotateCcw, FileText, FileImage, FileArchive, FileCode, Download, File,
         Users } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Parse a comma/semicolon separated list of email addresses into an array
function parseAddresses(raw) {
  if (!raw) return [];
  return raw.split(/[,;]/).map(a => a.trim()).filter(a => a.includes('@'));
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fileIcon(contentType) {
  if (!contentType) return <File size={14} />;
  if (contentType.startsWith('image/')) return <FileImage size={14} />;
  if (contentType === 'application/pdf') return <FileText size={14} />;
  if (contentType.includes('zip') || contentType.includes('compressed')) return <FileArchive size={14} />;
  if (contentType.includes('csv') || contentType.includes('json') || contentType.includes('xml')) return <FileCode size={14} />;
  return <FileText size={14} />;
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

function formatFullDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('it-IT', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch {
    return dateStr;
  }
}

export default function EmailView() {
  const { accountId, folder, uid } = useParams();
  const navigate = useNavigate();
  const { accounts, fetchEmailDetail, markEmailRead, sendEmail, openCompose } = useApp();
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyBody, setReplyBody] = useState('');
  const [replyMode, setReplyMode] = useState('reply'); // 'reply' | 'reply-all'
  const [replyCC, setReplyCC] = useState('');
  const [sending, setSending] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatedBody, setTranslatedBody] = useState(null); // null = show original
  const [replyTranslating, setReplyTranslating] = useState(false);
  const [replyTranslateLang, setReplyTranslateLang] = useState('inglese');
  const [attachments, setAttachments] = useState([]);
  const iframeRef = useRef(null);

  const account = accounts.find(a => a.id === accountId);

  useEffect(() => {
    loadEmail();
  }, [accountId, folder, uid]);

  const loadEmail = async () => {
    setLoading(true);
    setTranslatedBody(null);
    setAttachments([]);
    setReplyMode('reply');
    setReplyCC('');
    try {
      const data = await fetchEmailDetail(accountId, folder, uid);
      setEmail(data);
      // Load attachments
      try {
        const res = await axios.get(`${API}/emails/${accountId}/${folder}/${uid}/attachments`);
        setAttachments(res.data || []);
      } catch {}
    } catch (e) {
      toast.error('Impossibile caricare l\'email');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async () => {
    if (translatedBody !== null) { setTranslatedBody(null); return; }
    const textToTranslate = email.body_text || email.preview || email.subject || '';
    if (!textToTranslate.trim()) { toast.error('Nessun testo da tradurre'); return; }
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/translate`, { text: textToTranslate, target_language: 'italiano' });
      setTranslatedBody(res.data.translated);
      toast.success('Traduzione completata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la traduzione');
    } finally {
      setTranslating(false);
    }
  };

  const handleTranslateReply = async () => {
    if (!replyBody.trim()) { toast.error('Scrivi il testo prima di tradurre'); return; }
    setReplyTranslating(true);
    try {
      const res = await axios.post(`${API}/translate`, { text: replyBody, target_language: replyTranslateLang });
      setReplyBody(res.data.translated);
      toast.success(`Risposta tradotta in ${replyTranslateLang}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante la traduzione');
    } finally {
      setReplyTranslating(false);
    }
  };

  const LANGUAGES = [
    { code: 'inglese', label: 'Inglese' },
    { code: 'francese', label: 'Francese' },
    { code: 'tedesco', label: 'Tedesco' },
    { code: 'spagnolo', label: 'Spagnolo' },
    { code: 'portoghese', label: 'Portoghese' },
    { code: 'olandese', label: 'Olandese' },
    { code: 'russo', label: 'Russo' },
    { code: 'cinese', label: 'Cinese' },
    { code: 'giapponese', label: 'Giapponese' },
    { code: 'arabo', label: 'Arabo' },
    { code: 'polacco', label: 'Polacco' },
  ];

  const handleMarkUnread = async () => {
    try {
      await markEmailRead(accountId, folder, uid, false);
      setEmail(prev => ({ ...prev, is_read: 0 }));
      toast.success('Segnata come da leggere');
      navigate(-1);
    } catch {
      toast.error('Errore nel segnare come da leggere');
    }
  };

  const handleForward = () => {
    if (!email) return;
    const bodyText = email.body_text || '';
    const quotedBody = `\n\n\n--- Messaggio inoltrato ---\nDa: ${email.from_name ? email.from_name + ' <' + email.from_addr + '>' : email.from_addr}\nA: ${email.to_addrs || ''}\nData: ${formatFullDate(email.date_str)}\nOggetto: ${email.subject || ''}\n\n${bodyText}`;
    openCompose({
      mode: 'forward',
      accountId,
      subject: `Fwd: ${email.subject || ''}`,
      body: quotedBody,
    });
  };

  const handleReplyAll = () => {
    if (!email) return;
    const ownEmail = account?.email || '';
    // Collect all recipients from original To + CC, minus own account
    const allRecipients = [
      ...parseAddresses(email.to_addrs || ''),
      ...parseAddresses(email.cc_addrs || ''),
    ];
    const ccList = allRecipients
      .filter(addr => addr.toLowerCase() !== ownEmail.toLowerCase()
                   && addr.toLowerCase() !== email.from_addr?.toLowerCase())
      .join(', ');
    setReplyMode('reply-all');
    setReplyCC(ccList);
    document.getElementById('reply-box')?.scrollIntoView({ behavior: 'smooth' });
    setTimeout(() => document.getElementById('reply-textarea')?.focus(), 300);
  };

  const handleSendReply = async () => {
    if (!replyBody.trim()) {
      toast.error('Inserisci un messaggio prima di inviare');
      return;
    }
    setSending(true);
    try {
      await sendEmail({
        account_id: accountId,
        to_addr: email.from_addr,
        cc_addr: replyMode === 'reply-all' && replyCC ? replyCC : null,
        subject: `Re: ${email.subject || ''}`,
        body: replyBody,
        reply_to_message_id: email.message_id,
      });
      setReplyBody('');
      setReplyMode('reply');
      setReplyCC('');
      toast.success(replyMode === 'reply-all' ? 'Risposta inviata a tutti' : 'Risposta inviata con successo');
    } catch (e) {
      toast.error(`Errore invio: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  // Resize iframe to content
  const handleIframeLoad = () => {
    if (iframeRef.current) {
      try {
        const h = iframeRef.current.contentWindow.document.body.scrollHeight;
        iframeRef.current.style.height = Math.min(Math.max(h + 20, 200), 800) + 'px';
      } catch {}
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-muted-foreground" />
        <span className="ml-3 text-sm text-muted-foreground">Caricamento email...</span>
      </div>
    );
  }

  if (!email) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center">
        <p className="text-muted-foreground">Email non trovata</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-sm" style={{ color: '#E2725B' }}>
          Torna indietro
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap px-3 sm:px-6 py-3 pl-16 sm:pl-6 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
        <button
          data-testid="email-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mr-2"
        >
          <ArrowLeft size={16} />
          Indietro
        </button>

        <div className="flex-1 min-w-0" />

        <button
          data-testid="email-translate-btn"
          onClick={handleTranslate}
          disabled={translating}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium border rounded-sm transition-all hover:bg-accent"
          style={{ borderColor: 'hsl(var(--border))', color: translatedBody !== null ? '#E2725B' : undefined }}
          title={translatedBody !== null ? 'Mostra originale' : 'Traduci in italiano'}
        >
          {translating
            ? <Loader2 size={13} className="animate-spin" />
            : translatedBody !== null ? <RotateCcw size={13} /> : <Languages size={13} />
          }
          <span className="hidden sm:inline">{translating ? 'Traduzione...' : translatedBody !== null ? 'Originale' : 'Traduci'}</span>
        </button>
        <button
          data-testid="email-mark-unread-btn"
          onClick={handleMarkUnread}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium border rounded-sm transition-all hover:bg-accent"
          style={{ borderColor: 'hsl(var(--border))' }}
          title="Segna come da leggere"
        >
          <MailOpen size={13} />
          <span className="hidden sm:inline">Da leggere</span>
        </button>
        <button
          data-testid="email-forward-btn"
          onClick={handleForward}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium border rounded-sm transition-all hover:bg-accent"
          style={{ borderColor: 'hsl(var(--border))' }}
          title="Inoltra"
        >
          <Forward size={13} />
          <span className="hidden sm:inline">Inoltra</span>
        </button>
        <button
          data-testid="email-reply-all-btn"
          onClick={handleReplyAll}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium border rounded-sm transition-all hover:bg-accent"
          style={{ borderColor: 'hsl(var(--border))' }}
          title="Rispondi a tutti i destinatari (To + CC)"
        >
          <Users size={13} />
          <span className="hidden sm:inline">Rispondi a tutti</span>
        </button>
        <button
          data-testid="email-reply-scroll-btn"
          onClick={() => {
            setReplyMode('reply');
            setReplyCC('');
            document.getElementById('reply-box')?.scrollIntoView({ behavior: 'smooth' });
            setTimeout(() => document.getElementById('reply-textarea')?.focus(), 300);
          }}
          className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-white rounded-sm transition-all"
          style={{ background: '#E2725B' }}
          onMouseEnter={e => e.currentTarget.style.background = '#d4644d'}
          onMouseLeave={e => e.currentTarget.style.background = '#E2725B'}
          title="Rispondi"
        >
          <CornerUpLeft size={13} />
          <span className="hidden sm:inline">Rispondi</span>
        </button>
      </div>

      {/* Email content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Subject */}
          <h1 className="text-2xl font-serif mb-6" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="email-subject">
            {email.subject || '(Nessun oggetto)'}
          </h1>

          {/* Metadata */}
          <div className="flex flex-wrap items-start gap-4 mb-6 pb-6 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-12">Da:</span>
                <span className="text-sm font-medium" data-testid="email-from">
                  {email.from_name && email.from_name !== email.from_addr
                    ? `${email.from_name} <${email.from_addr}>`
                    : email.from_addr}
                </span>
              </div>
              {email.to_addrs && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-12">A:</span>
                  <span className="text-sm text-muted-foreground truncate">{email.to_addrs}</span>
                </div>
              )}
              {email.cc_addrs && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-12">Cc:</span>
                  <span className="text-sm text-muted-foreground truncate">{email.cc_addrs}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-12">Data:</span>
                <span className="text-xs text-muted-foreground">{formatFullDate(email.date_str)}</span>
              </div>
            </div>

            {/* Account badge */}
            {account && (
              <div
                data-testid="email-account-badge"
                className="account-badge flex-shrink-0"
                style={{
                  background: `${account.color}22`,
                  color: account.color,
                  border: `1px solid ${account.color}44`,
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: account.color }} />
                {account.email}
              </div>
            )}
          </div>

          {/* Attachments */}
          {attachments.length > 0 && (
            <div className="mb-6 p-4 rounded-xl border" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(40,18%,98%)' }}>
              <div className="flex items-center gap-2 mb-3">
                <Paperclip size={13} className="text-muted-foreground" />
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {attachments.length} allegat{attachments.length === 1 ? 'o' : 'i'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {attachments.map(att => (
                  <a
                    key={att.id}
                    href={`${API}/attachments/${att.email_cache_id}/${att.id}/download`}
                    download={att.filename}
                    data-testid={`attachment-download-${att.id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium transition-all hover:shadow-sm"
                    style={{ borderColor: 'hsl(var(--border))', background: 'white', color: '#444', textDecoration: 'none', maxWidth: '220px' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#E2725B'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'hsl(var(--border))'}
                  >
                    <span style={{ color: '#E2725B' }}>{fileIcon(att.content_type)}</span>
                    <span className="truncate flex-1">{att.filename}</span>
                    <span className="text-gray-400 flex-shrink-0">{formatBytes(att.size)}</span>
                    <Download size={11} className="text-gray-400 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Body */}
          <div className="mb-8" data-testid="email-body">
            {translatedBody !== null ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full"
                    style={{ background: '#EDE8FF', color: '#6B6FD8' }}>
                    <Languages size={11} /> Tradotto in italiano
                  </span>
                </div>
                <pre className="whitespace-pre-wrap text-sm leading-relaxed" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {translatedBody}
                </pre>
              </div>
            ) : email.body_html ? (
              <div className="rounded-sm overflow-hidden border" style={{ borderColor: 'hsl(var(--border))' }}>
                <iframe
                  ref={iframeRef}
                  srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><style>
                    body { font-family: Arial, sans-serif; font-size: 14px; line-height: 1.6;
                           margin: 16px; color: #333; max-width: 100%; overflow-x: hidden; }
                    img { max-width: 100%; height: auto; }
                    a { color: #E2725B; }
                    pre { white-space: pre-wrap; word-wrap: break-word; }
                  </style></head><body>${email.body_html}</body></html>`}
                  sandbox="allow-same-origin allow-popups"
                  className="email-iframe"
                  title="Contenuto email"
                  onLoad={handleIframeLoad}
                  style={{ minHeight: '200px', height: '400px' }}
                />
              </div>
            ) : email.body_text ? (
              <pre
                className="whitespace-pre-wrap text-sm leading-relaxed"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                {email.body_text}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground italic">Nessun contenuto</p>
            )}
          </div>

          {/* Reply box */}
          <div
            id="reply-box"
            data-testid="reply-box"
            className="rounded-sm border p-4"
            style={{ borderColor: 'hsl(var(--border))' }}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                {replyMode === 'reply-all'
                  ? <><Users size={14} style={{ color: '#E2725B' }} />Rispondi a tutti</>
                  : <><CornerUpLeft size={14} style={{ color: '#E2725B' }} />Rispondi</>
                }
              </h3>
              {/* Toggle reply / reply-all */}
              <div className="flex items-center gap-1 p-0.5 rounded-lg" style={{ background: '#F0ECE8' }}>
                <button
                  data-testid="reply-mode-reply"
                  onClick={() => { setReplyMode('reply'); setReplyCC(''); }}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all"
                  style={{
                    background: replyMode === 'reply' ? 'white' : 'transparent',
                    color: replyMode === 'reply' ? '#333' : '#888',
                    boxShadow: replyMode === 'reply' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                  }}>
                  <CornerUpLeft size={11} /> Solo mittente
                </button>
                <button
                  data-testid="reply-mode-all"
                  onClick={handleReplyAll}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md transition-all"
                  style={{
                    background: replyMode === 'reply-all' ? 'white' : 'transparent',
                    color: replyMode === 'reply-all' ? '#333' : '#888',
                    boxShadow: replyMode === 'reply-all' ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                  }}>
                  <Users size={11} /> A tutti
                </button>
              </div>
            </div>

            {/* From field - locked */}
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16 flex-shrink-0">Da:</span>
              <div className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm border flex-1"
                style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                <span className="flex-1">{account?.email || accountId}</span>
              </div>
            </div>

            {/* To field */}
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16 flex-shrink-0">A:</span>
              <div className="px-3 py-1.5 text-sm rounded-sm border flex-1"
                style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                {email.from_addr}
              </div>
            </div>

            {/* CC field (visible in reply-all mode OR if cc_addrs exist) */}
            {(replyMode === 'reply-all') && (
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xs text-muted-foreground w-16 flex-shrink-0">CC:</span>
                <input
                  data-testid="reply-cc-input"
                  type="text"
                  value={replyCC}
                  onChange={e => setReplyCC(e.target.value)}
                  placeholder="Destinatari in copia (modifica se necessario)"
                  className="px-3 py-1.5 text-sm rounded-sm border flex-1 focus:outline-none transition-all"
                  style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--background))' }}
                  onFocus={e => e.target.style.borderColor = '#E2725B'}
                  onBlur={e => e.target.style.borderColor = 'hsl(var(--border))'}
                />
              </div>
            )}

            {/* Subject */}
            <div className="mb-3 flex items-center gap-2">
              <span className="text-xs text-muted-foreground w-16 flex-shrink-0">Oggetto:</span>
              <div className="px-3 py-1.5 text-sm rounded-sm border flex-1"
                style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                Re: {email.subject || ''}
              </div>
            </div>

            {/* Body */}
            <textarea
              id="reply-textarea"
              data-testid="reply-textarea"
              value={replyBody}
              onChange={e => setReplyBody(e.target.value)}
              placeholder="Scrivi la tua risposta..."
              className="w-full min-h-[120px] px-3 py-2.5 text-sm rounded-sm border resize-y focus:outline-none focus:ring-1 transition-all"
              style={{
                borderColor: 'hsl(var(--border))',
                background: 'hsl(var(--background))',
                fontFamily: 'Manrope, sans-serif',
              }}
              onFocus={e => e.target.style.borderColor = '#E2725B'}
              onBlur={e => e.target.style.borderColor = 'hsl(var(--border))'}
            />

            {/* Translation bar */}
            <div className="flex items-center gap-2 mt-2 pt-2 border-t flex-wrap" style={{ borderColor: 'hsl(var(--border))' }}>
              <Languages size={13} className="text-muted-foreground flex-shrink-0" />
              <span className="text-xs text-muted-foreground flex-shrink-0">Traduci in:</span>
              <select
                data-testid="reply-translate-lang"
                value={replyTranslateLang}
                onChange={e => setReplyTranslateLang(e.target.value)}
                className="text-xs border rounded px-2 py-1 focus:outline-none min-w-0 flex-1 sm:flex-initial"
                style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--background))' }}
              >
                {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
              <button
                data-testid="reply-translate-btn"
                onClick={handleTranslateReply}
                disabled={replyTranslating || !replyBody.trim()}
                className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium border rounded transition-all hover:bg-accent flex-shrink-0"
                style={{ borderColor: 'hsl(var(--border))', opacity: replyTranslating || !replyBody.trim() ? 0.5 : 1 }}
              >
                {replyTranslating ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
                {replyTranslating ? 'Traduco...' : 'Traduci'}
              </button>
            </div>

            <div className="flex items-center justify-end gap-2 mt-3 pb-12 sm:pb-0">
              <button
                data-testid="send-reply-btn"
                onClick={handleSendReply}
                disabled={sending || !replyBody.trim()}
                className="flex items-center justify-center gap-2 px-5 py-2 text-sm font-medium text-white rounded-sm transition-all w-full sm:w-auto"
                style={{
                  background: '#E2725B',
                  opacity: sending || !replyBody.trim() ? 0.6 : 1,
                  cursor: sending || !replyBody.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {sending ? (
                  <><Loader2 size={13} className="animate-spin" />Invio...</>
                ) : replyMode === 'reply-all' ? (
                  <><Users size={13} />Invia a tutti</>
                ) : (
                  <><Send size={13} />Invia risposta</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
